import { useState, FormEvent } from "react";
import Toast from "@/components/base/Toast";
import { usePhoneMask } from "@/hooks/usePhoneMask";
import { site } from "@/content";
import { interpolate } from "@/lib/rich";
import { useTheme } from "@/theme";

interface IntegrationLeadFormProps {
  title: string;
  subtitle: string;
  formName: string;
  integrationTitle?: string;
  compact?: boolean;
}

export default function IntegrationLeadForm({
  title,
  subtitle,
  formName,
  integrationTitle,
  compact = false,
}: IntegrationLeadFormProps) {
  const labels = site.leadForm;
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    type: "success" as "success" | "error",
    visible: false,
  });
  const phoneMask = usePhoneMask();

  const successText = interpolate(labels.successTextTemplate, {
    suffix: integrationTitle ? ` «${integrationTitle}»` : "",
  });

  /* Оформление зависит от темы: разметка одна, классы разные */
  const ui = isDark
    ? {
        card: "bg-white/[0.04] backdrop-blur-xl border border-white/[0.09] rounded-3xl",
        title: "text-white",
        subtitle: "text-white/60",
        label: "text-white/60",
        input:
          "w-full bg-white/[0.05] border border-white/[0.14] rounded-xl px-4 py-3 text-white placeholder-white/40 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-500/25 transition-all",
        checkbox:
          "mt-0.5 w-4 h-4 rounded border-white/30 bg-white/[0.05] text-orange-500 focus:ring-orange-400 focus:ring-2 cursor-pointer",
        consent: "text-white/60 text-xs leading-relaxed",
        consentLink: "text-orange-400 hover:text-orange-300 underline",
        submit:
          "w-full py-3.5 rounded-xl bg-orange-500 text-[#0A0A0A] font-bold text-sm hover:bg-orange-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed",
        successCircle: "bg-emerald-500/[0.15]",
        successIcon: "text-emerald-400",
        successTitle: "text-white",
        successText: "text-white/60",
        again: "text-orange-400 hover:text-orange-300",
      }
    : {
        card: "bg-white border border-stone-200 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)]",
        title: "text-stone-900",
        subtitle: "text-stone-600",
        label: "text-stone-500",
        input:
          "w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition-all",
        checkbox:
          "mt-0.5 w-4 h-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500 focus:ring-2 cursor-pointer",
        consent: "text-stone-500 text-xs leading-relaxed",
        consentLink: "text-amber-700 hover:text-amber-600 underline",
        submit:
          "w-full py-3.5 rounded-xl bg-stone-900 text-white font-bold text-sm hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed",
        successCircle: "bg-emerald-100",
        successIcon: "text-emerald-600",
        successTitle: "text-stone-900",
        successText: "text-stone-600",
        again: "text-amber-700 hover:text-amber-600",
      };

  const pad = compact ? "p-6" : "p-8 md:p-10";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!agreed) {
      setToast({ message: labels.errorConsent, type: "error", visible: true });
      return;
    }

    const formEl = event.currentTarget;
    setLoading(true);

    const data = new URLSearchParams(
      new FormData(formEl) as unknown as Record<string, string>
    );
    data.append("page_path", window.location.pathname);
    data.append("form_name", formName);
    if (integrationTitle) data.append("integration", integrationTitle);

    try {
      await fetch(site.webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setToast({ message: labels.successToast, type: "success", visible: true });
      setSubmitted(true);
      formEl.reset();
      setAgreed(false);
    } catch {
      setToast({ message: labels.errorGeneric, type: "error", visible: true });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={`${ui.card} ${pad}`}>
        <div className="text-center py-6">
          <div
            className={`w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-4 ${ui.successCircle}`}
          >
            <i className={`ri-check-line text-2xl ${ui.successIcon}`}></i>
          </div>
          <div className={`font-bold text-lg mb-2 ${ui.successTitle}`}>
            {labels.successTitle}
          </div>
          <p className={`text-sm leading-relaxed mb-5 ${ui.successText}`}>
            {successText}
          </p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className={`text-sm font-semibold transition-colors cursor-pointer ${ui.again}`}
          >
            {labels.successAgain}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${ui.card} ${pad}`}>
      <div className="mb-6">
        <h3 className={`font-bold text-lg mb-1.5 ${ui.title}`}>{title}</h3>
        <p className={`text-sm leading-relaxed ${ui.subtitle}`}>{subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className={compact ? "" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
          <div className={compact ? "mb-4" : ""}>
            <label
              className={`block text-xs font-semibold mb-2 uppercase tracking-wide ${ui.label}`}
            >
              {labels.name}
            </label>
            <input
              type="text"
              name="name"
              required
              placeholder={labels.namePlaceholder}
              className={ui.input}
            />
          </div>
          <div>
            <label
              className={`block text-xs font-semibold mb-2 uppercase tracking-wide ${ui.label}`}
            >
              {labels.phone}
            </label>
            <input
              type="tel"
              name="phone"
              required
              placeholder={labels.phonePlaceholder}
              {...phoneMask}
              className={ui.input}
            />
          </div>
        </div>

        <div>
          <label
            className={`block text-xs font-semibold mb-2 uppercase tracking-wide ${ui.label}`}
          >
            {labels.comment}
          </label>
          <textarea
            name="message"
            rows={compact ? 2 : 3}
            maxLength={500}
            placeholder={labels.commentPlaceholder}
            className={`${ui.input} resize-none`}
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            className={ui.checkbox}
          />
          <ConsentText
            label={labels.consent}
            className={ui.consent}
            linkClassName={ui.consentLink}
          />
        </label>

        <button type="submit" disabled={loading || !agreed} className={ui.submit}>
          {loading ? labels.submitting : labels.submit}
        </button>
      </form>

      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onClose={() => setToast((state) => ({ ...state, visible: false }))}
      />
    </div>
  );
}

/** Согласие с политикой — акцентная часть становится ссылкой */
function ConsentText({
  label,
  className,
  linkClassName,
}: {
  label: string;
  className: string;
  linkClassName: string;
}) {
  const parts = label.split("**");
  return (
    <span className={className}>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <a
            key={index}
            href={site.policyUrl}
            target="_blank"
            rel="noreferrer"
            className={linkClassName}
          >
            {part}
          </a>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  );
}
